<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>{{ $t('users_crud.user_management') }}</v-toolbar-title>
      <v-divider class="mx-2" inset vertical></v-divider>
      <v-spacer></v-spacer>
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" dark class="mb-2" v-on="on">{{ $t('users_crud.new_user') }}</v-btn>
        </template>
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    type="email"
                    v-model="editedItem.email"
                    :rules="emailRules"
                    :label="$t('users_crud.email')"
                    :readonly="!formAction"
                    autocomplete="new-password"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    type="text"
                    v-model="editedItem.name"
                    :rules="nameRules"
                    :label="$t('users_crud.name')"
                    autocomplete="new-password"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    v-model="password"
                    type="password"
                    :rules="passwordRules"
                    :label="$t('users_crud.password')"
                    autocomplete="new-password"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-text-field
                    v-model="repeatedPassword"
                    type="password"
                    :rules="repeatedPasswordRules"
                    :label="$t('users_crud.repeat_password')"
                    autocomplete="new-password"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs24 sm12 md12>
                  <v-select
                    v-model="editedItem.type"
                    :items="userTypes"
                    :label="$t('users_crud.type')"
                  ></v-select>
                </v-flex>
                <div
                  class="password_warning"
                  v-if="!passwordStrong"
                >*{{$t('users_crud.password_requirements')}}</div>
                <div
                  class="passwords_do_not_match"
                  v-if="showPasswordsMatchWarning"
                >{{$t('users_crud.passwords_do_not_match')}}</div>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" flat @click="close">{{ $t('users_crud.cancel') }}</v-btn>
            <v-btn
              color="blue darken-1"
              flat
              @click="save"
              v-if="formAction && passwordsMatch"
              :disabled="valid"
            >{{ $t('users_crud.save') }}</v-btn>
            <v-btn
              color="blue darken-1"
              flat
              @click="edit"
              v-if="!formAction && passwordsMatch"
            >{{ $t('users_crud.edit') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <div class="table-wrapper">
      <div class="progress-wrapper">
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
      </div>
      <div class="rTable">
        <div class="rTableRow problem-header">
          <div
            class="rTableHead"
            v-for="(header, i) in headers"
            :key="i"
            :style="{ 'width': header.width + '%'}"
          >{{header.text}}</div>
        </div>
        <div class="rTableRow" v-for="(user, i ) in users" :key="i">
          <div class="rTableCell">{{ user.name }}</div>
          <div class="rTableCell">{{ user.email }}</div>
          <div class="rTableCell">{{ getTypeString(user.type) }}</div>
          <div class="rTableCell">
            <v-icon small class="mr-2" @click="editItem(user)" v-if="canEdit(user)">edit</v-icon>
            <v-icon small @click="deleteItem(user)" v-if="canEdit(user)">delete</v-icon>
            <v-icon small v-if="!canEdit(user)">block</v-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import User from "@/models/User.js";
export default {
  data() {
    return {
      loading: false,
      dialog: false,
      valid: false,
      headers: [],
      users: [],
      pagination: {
        rowsPerPage: 50,
        sortBy: "type",
      },
      rowsPerPageItems: [],
      editedIndex: -1,
      editedItem: {
        name: "",
        email: "",
        type: 2,
      },
      defaultItem: {
        name: "",
        email: "",
        type: 2,
      },
      emailRules: [],
      nameRules: [],
      passwordRules: [],
      repeatedPasswordRules: [],
      typeRules: [],
      password: "",
      repeatedPassword: "",
      user: new User(this.$session.get("user")),
      userTypes: [
        {
          text: this.$t("users_crud.planner"),
          value: 2,
        },
        {
          text: this.$t("users_crud.manager"),
          value: 3,
        },
        {
          text: this.$t("users_crud.packer"),
          value: 4,
        },
        {
          text: this.$t("users_crud.worker"),
          value: 5,
        },
        {
          text: this.$t("users_crud.read_only"),
          value: 6,
        },
      ],
    };
  },

  computed: {
    /**
     * determines which form action is happening
     */
    formAction() {
      return this.editedIndex === -1 ? true : false;
    },

    /**
     * return title proper to action
     */
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("users_crud.new_user")
        : this.$t("users_crud.edit_user");
    },

    /**
     * options of items per page
     */
    perPage() {
      return [5, 10, 25];
    },

    /**
      returns true if password is strong enough
     */
    passwordStrong() {
      //eslint-disable-next-line
      const specialSymbols = /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
      const numbers = /\d/;
      const lowerCase = /[a-z]/;
      const upperCase = /[A-Z]/;
      const hasSpecialSymbols = specialSymbols.test(this.password); // check if has at least one special character
      const hasNumber = numbers.test(this.password); // check if password has atleast one number
      const hasLowerCase = lowerCase.test(this.password); // check if has at least one lowercase letter
      const hasUpperCase = upperCase.test(this.password); // check if has at least one uppercase letter
      const isLongEnough = this.password.length >= 8; // check string length
      return (
        hasSpecialSymbols &&
        hasNumber &&
        hasLowerCase &&
        hasUpperCase &&
        isLongEnough
      );
    },
    /**
     * show if passwords do not match
     */
    showPasswordsMatchWarning() {
      if (!this.passwordStrong) {
        return false;
      }
      return this.password != this.repeatedPassword;
    },

    /**
     * returns true if passwords match
     */
    passwordsMatch() {
      return this.password == this.repeatedPassword;
    },
  },

  watch: {
    /**
     * watches dialog changes
     */
    dialog(val) {
      val || this.close();
    },
  },

  created() {
    this.initTexts();
    this.initialize();
  },

  methods: {
    /**
     * loads texts
     */
    initTexts() {
      this.headers = [
        { text: this.$t("users_crud.name"), value: "name", align: "left" },
        { text: this.$t("users_crud.email"), value: "email", align: "left" },
        { text: this.$t("users_crud.type"), value: "type", align: "left" },
        {
          text: this.$t("users_crud.actions"),
          value: "actions",
          sortable: false,
        },
      ];
      this.rowsPerPageItems = [
        5,
        10,
        25,
        { text: this.$t("users_crud.all"), value: -1 },
      ];
      this.emailRules = [
        (v) => !!v || this.$t("users_crud.email_required"),
        (v) => /.+@.+/.test(v) || this.$t("users_crud.valid_email_required"),
      ];
      this.nameRules = [(v) => !!v || this.$t("users_crud.name_required")];
      this.passwordRules = [
        (v) => !!v || this.$t("users_crud.password_required"),
      ];
      this.repeatedPasswordRules = [
        (v) => !!v || this.$t("users_crud.please_repeat_password"),
      ];
      this.typeRules = [(v) => !!v || this.$t("users_crud.type_required")];
    },

    getTypeString(userType) {
      switch (userType) {
        case 0:
          return this.$t("users_crud.super");
        case 1:
          return this.$t("users_crud.admin");
        case 2:
          return this.$t("users_crud.planner");
        case 3:
          return this.$t("users_crud.manager");
        case 4:
          return this.$t("users_crud.packer");
        case 5:
          return this.$t("users_crud.worker");
        case 6:
          return this.$t("users_crud.read_only");
      }
      return "";
    },

    /**
     * loads a list of users
     */
    async initialize() {
      this.loading = true;
      const users = await User.loadUsers();
      this.users = users.sort((a, b) => {
        return a.type - b.type;
      });
      this.loading = false;
    },

    /**
     * adds user to the database
     */
    async addUser() {
      try {
        await User.create({
          email: this.editedItem.email,
          password: this.password,
          name: this.editedItem.name,
          type: this.editedItem.type,
          company_id: this.user.company_id,
        });
        this.initialize();
        this.password = "";
        this.repeatedPassword = "";
      } catch {
        alert(this.$t("users_crud.post_error"));
      }
    },

    /**
     * edits user in the database
     */
    async editUser() {
      let data = {
        name: this.editedItem.name,
      };
      if (this.editedItem.type != null && this.user.id != this.editedItem.id) {
        data["type"] = this.editedItem.type;
      }
      if (this.password != "") {
        data["password"] = this.password;
      }
      try {
        await this.editedItem.save(data);
        this.initialize();
        this.password = "";
        this.repeatedPassword = "";
      } catch {
        alert(this.$t("users_crud.edit_error"));
      }
    },

    /**
     * delets user from the database
     */
    async deleteUser(user) {
      const deletingUser = new User(user);
      await deletingUser.delete();
      this.initialize();
    },

    /**
     * assigns item to work with, opens dialog
     */
    editItem(item) {
      this.editedIndex = item.id;
      this.editedItem = new User(item);
      this.dialog = true;
    },

    /**
     * triggers save function, closes dialog
     */
    save() {
      this.addUser();
      this.close();
    },

    /**
     * triggers edit function, closes dialog
     */
    edit() {
      this.editUser();
      this.close();
    },

    /**
     * deletes item
     */
    deleteItem(item) {
      confirm(this.$t("users_crud.do_you_really_want_to_delete")) &&
        //delete if confirms
        this.deleteUser(item);
    },

    /**
     * closes dialog, clears text box form data
     */
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.password = "";
        this.passwordRepeat = "";
      }, 300);
    },

    /**
     * determines if user can edit other user
     */
    canEdit(item) {
      if (this.user.id == item.id) {
        return true;
      }
      if (this.user["type"] == 0 && item["type"] != 0) {
        return true;
      }
      if (this.user["type"] == 1 && item["type"] >= 2) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style scoped>
.password_warning {
  color: brown;
  font-weight: bold;
}
.passwords_do_not_match {
  color: brown;
  font-weight: bold;
}
table.v-table tbody td,
table.v-table tbody th {
  height: 10px;
}
</style>


